import { trpcClient } from './trpc'

// Internal queries for web-admin only
export const gameQueries = {
  // Get game data by ID (directly from database)
  getGameDataById: async (gameId: string) => {
    return await trpcClient.queryRouter.getGameDataById.query({ gameId })
  },

  // Get games by multiple filters with AND logic
  getGamesByFilters: async (filters: {
    languages?: string[]
    statuses?: string[]
    models?: string[]
    userId?: string
    gameId?: string
    tags?: Array<'game' | 'data'>
  }) => {
    return await trpcClient.queryRouter.getGamesByFilters.query(filters)
  },

  // Get all possible filter options from database
  getFilterOptions: async () => {
    return await trpcClient.queryRouter.getFilterOptions.query()
  },

  // Get recent games with pagination
  getRecentGames: async (page = 1, limit = 10) => {
    return await trpcClient.queryRouter.getRecentGames.query({ page, limit })
  },
  // Refresh responsive models cache
  refreshResponsiveModelsCache: async () => {
    return await trpcClient.queryRouter.refreshResponsiveModelsCache.mutate()
  },

  // Get responsive models from cache
  getResponsiveModels: async () => {
    return await trpcClient.queryRouter.getResponsiveModels.query()
  },

  getModelConfig: async () => {
    return await trpcClient.queryRouter.getModelConfig.query()
  },

  getGameModelPools: async () => {
    return await trpcClient.queryRouter.getGameModelPools.query()
  },

  setGameModelPools: async (input: {
    lowCost?: Record<string, string[]>
    highPerformance?: Record<string, string[]>
  }) => {
    return await trpcClient.queryRouter.setGameModelPools.mutate(input)
  },

  getBlockedKeywords: async () => {
    return await trpcClient.queryRouter.getBlockedKeywords.query()
  },

  addBlockedKeyword: async (keyword: string, field: 'name' | 'email' | 'displayName') => {
    return await trpcClient.queryRouter.addBlockedKeyword.mutate({ keyword, field })
  },

  removeBlockedKeyword: async (keyword: string, field: 'name' | 'email' | 'displayName') => {
    return await trpcClient.queryRouter.removeBlockedKeyword.mutate({ keyword, field })
  },

  // Whitelist emails
  getWhitelistEmails: async () => {
    return await trpcClient.queryRouter.getWhitelistEmails.query()
  },

  addWhitelistEmail: async (email: string) => {
    return await trpcClient.queryRouter.addWhitelistEmail.mutate({ email })
  },

  removeWhitelistEmail: async (email: string) => {
    return await trpcClient.queryRouter.removeWhitelistEmail.mutate({ email })
  },

  // Token cost / model pricing
  getModelPricing: async () => {
    return await trpcClient.queryRouter.getModelPricing.query()
  },

  syncModelPricing: async () => {
    return await trpcClient.queryRouter.syncModelPricing.mutate()
  },

  // Model catalog sync (OpenRouter)
  fetchModelCatalogDiff: async () => {
    return await trpcClient.queryRouter.fetchModelCatalogDiff.mutate()
  },

  probeModels: async (models: string[], timeoutMs?: number) => {
    return await trpcClient.queryRouter.probeModels.mutate({ models, timeoutMs })
  },

  applyModelConfigUpdate: async (
    modelConfig: Record<string, string[]>,
    refreshResponsive?: boolean,
  ) => {
    return await trpcClient.queryRouter.applyModelConfigUpdate.mutate({ modelConfig, refreshResponsive })
  },

  // Credit reconciliation
  getReconcileStatus: async () => {
    return await trpcClient.queryRouter.getReconcileStatus.query()
  },

  reconcileUser: async (userId: string) => {
    return await trpcClient.queryRouter.reconcileUser.mutate({ userId })
  },
}

// Types for the queries
export type GameDataStructure = Record<string, unknown>

export type GameData = {
  game: {
    id: string
    status: string
    gameSetting: string
    gameType: string
    goodModel: string
    evilModel: string
    winner: string | null
    data: GameDataStructure | null
    godLog: string
    systemPrompt: string
    createdAt: Date | string
    updatedAt: Date | string
  } | null
  players: Array<{
    id: string
    name: string
    position: number
    role: string
    faction: string
    modelName: string
    status: string
    log: string
    createdAt: Date | string
    updatedAt: Date | string
  }>
  actions: Array<{
    id: string
    playerId: string
    playerName: string
    playerRole: string
    roundId: string
    actionType: string
    input: string
    output: string
    createdAt: Date | string
    updatedAt: Date | string
    round: {
      roundNumber: number
      phase: string
      subPhase: string
    }
    roundNumber: number
    phase: string
    subPhase: string
  }>
}

export type FilterGame = {
  id: string
  status: string
  gameSetting: string
  gameType: string
  goodModel: string
  evilModel: string
  winner: string | null
  data: GameDataStructure | null
  createdAt: Date | string
  updatedAt: Date | string
}
